import { useMemo } from "react";
import { Building2, Sparkles, User as UserIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { DateRangePicker, FilterBar, FilterChip, type DateRangeValue } from "@/components/dashboard";

type Apartment = { id: number; name: string };
type Person = { id: number; fullName?: string | null; username: string; isActive?: boolean };

export type ReportsFilterBarProps = {
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;

  apartmentFilter: string;
  onApartmentChange: (id: string) => void;
  apartments: Apartment[];

  ownerFilter: string;
  onOwnerChange: (id: string) => void;
  owners: Person[];

  cleanerFilter: string;
  onCleanerChange: (id: string) => void;
  cleaners: Person[];

  showCleaner?: boolean;
};

function personLabel(p: Person): string {
  const base = p.fullName || p.username;
  return p.isActive === false ? `${base} (inactive)` : base;
}

function nameById(id: string, list: Array<{ id: number; name?: string }>, label: (item: { id: number; name?: string }) => string): string | null {
  if (id === "all") return null;
  const item = list.find(x => String(x.id) === id);
  return item ? label(item) : id;
}

export function ReportsFilterBar(props: ReportsFilterBarProps) {
  const {
    dateRange, onDateRangeChange,
    apartmentFilter, onApartmentChange, apartments,
    ownerFilter, onOwnerChange, owners,
    cleanerFilter, onCleanerChange, cleaners,
    showCleaner = true,
  } = props;

  const aptOptions = useMemo(
    () => [{ value: "all", label: "All Apartments" }, ...apartments.map(a => ({ value: String(a.id), label: a.name }))],
    [apartments],
  );
  const ownerOptions = useMemo(
    () => [{ value: "all", label: "All Property Managers" }, ...owners.map(o => ({ value: String(o.id), label: personLabel(o) }))],
    [owners],
  );
  const cleanerOptions = useMemo(
    () => [{ value: "all", label: "All Cleaners" }, ...cleaners.map(c => ({ value: String(c.id), label: personLabel(c) }))],
    [cleaners],
  );

  const apartmentLabel = nameById(apartmentFilter, apartments, a => a.name ?? `#${a.id}`);
  const ownerName = ownerFilter === "all" ? null : (owners.find(o => String(o.id) === ownerFilter) ? personLabel(owners.find(o => String(o.id) === ownerFilter)!) : ownerFilter);
  const cleanerName = cleanerFilter === "all" ? null : (cleaners.find(c => String(c.id) === cleanerFilter) ? personLabel(cleaners.find(c => String(c.id) === cleanerFilter)!) : cleanerFilter);

  const hasActiveFilters =
    !!(dateRange.from || dateRange.to)
    || apartmentFilter !== "all"
    || ownerFilter !== "all"
    || (showCleaner && cleanerFilter !== "all");

  const clearAll = () => {
    onDateRangeChange({ from: null, to: null });
    onApartmentChange("all");
    onOwnerChange("all");
    if (showCleaner) onCleanerChange("all");
  };

  return (
    <Card className="no-print">
      <CardContent className="p-4">
        <FilterBar onClearAll={clearAll} hasActiveFilters={hasActiveFilters}>
          <DateRangePicker value={dateRange} onChange={onDateRangeChange} />

          <FilterChip
            label="Apartment"
            value={apartmentLabel}
            onClear={apartmentFilter !== "all" ? () => onApartmentChange("all") : undefined}
            testId="filter-apartment"
          >
            <div className="w-[18rem]">
              <div className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground">
                <Building2 className="h-3 w-3" /> Apartment
              </div>
              <SearchableSelect
                value={apartmentFilter}
                onValueChange={onApartmentChange}
                options={aptOptions}
                placeholder="Select apartment"
              />
            </div>
          </FilterChip>

          <FilterChip
            label="PM"
            value={ownerName}
            onClear={ownerFilter !== "all" ? () => onOwnerChange("all") : undefined}
            testId="filter-owner"
          >
            <div className="w-[18rem]">
              <div className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground">
                <UserIcon className="h-3 w-3" /> Property Manager
              </div>
              <SearchableSelect
                value={ownerFilter}
                onValueChange={onOwnerChange}
                options={ownerOptions}
                placeholder="Select PM"
              />
            </div>
          </FilterChip>

          {showCleaner && (
            <FilterChip
              label="Cleaner"
              value={cleanerName}
              onClear={cleanerFilter !== "all" ? () => onCleanerChange("all") : undefined}
              testId="filter-cleaner"
            >
              <div className="w-[18rem]">
                <div className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground">
                  <Sparkles className="h-3 w-3" /> Cleaner
                </div>
                <SearchableSelect
                  value={cleanerFilter}
                  onValueChange={onCleanerChange}
                  options={cleanerOptions}
                  placeholder="Select cleaner"
                />
              </div>
            </FilterChip>
          )}
        </FilterBar>
      </CardContent>
    </Card>
  );
}

export default ReportsFilterBar;
